/**
 * TalentLens AI - Pipeline Page
 * Kanban board for tracking candidates through interview stages
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import api from '../lib/api';
import { PIPELINE_STAGE_CONFIG } from '@talentlens/shared';

type PipelineStage = 'applied' | 'phone_screen' | 'interview' | 'technical' | 'final' | 'offer' | 'hired' | 'rejected';

interface CandidateCard {
  id: string;
  cvId: string;
  stage: PipelineStage;
  rating: number;
  candidate: {
    name: string;
    email?: string;
    skills: { skill: string; years: number }[];
  };
  interviewsCount: number;
  latestInterview?: {
    title: string;
    scheduled_at: string;
    status: string;
  };
  createdAt: string;
}

export default function PipelinePage() {
  const params = useParams({ strict: false }) as { jobId: string };
  const jobId = params.jobId;
  const queryClient = useQueryClient();
  const [showRejected, setShowRejected] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateCard | null>(null);
  const [draggedCard, setDraggedCard] = useState<CandidateCard | null>(null);

  const { data: jobData, isLoading: jobLoading } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api.getJob(jobId),
    enabled: !!jobId,
  });

  const { data: pipelineData, isLoading: pipelineLoading } = useQuery({
    queryKey: ['pipeline', jobId, showRejected],
    queryFn: () => api.getPipelineForJob(jobId, { includeRejected: showRejected ? 'true' : 'false' }),
    enabled: !!jobId,
  });

  const { data: statsData } = useQuery({
    queryKey: ['pipeline-stats', jobId],
    queryFn: () => api.getPipelineStats(jobId),
    enabled: !!jobId,
  });

  const updateStageMutation = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: string }) => api.updatePipelineStage(id, { stage }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipeline', jobId] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-stats', jobId] });
    },
  });

  const job = jobData?.data;
  const candidates: CandidateCard[] = pipelineData?.data || [];
  const stats = statsData?.data;

  // Group candidates by stage
  const candidatesByStage = PIPELINE_STAGE_CONFIG.reduce((acc, stage) => {
    acc[stage.id as PipelineStage] = candidates.filter(c => c.stage === stage.id);
    return acc;
  }, {} as Record<PipelineStage, CandidateCard[]>);

  const handleDragStart = (e: React.DragEvent, card: CandidateCard) => {
    setDraggedCard(card);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetStage: PipelineStage) => {
    e.preventDefault();
    if (draggedCard && draggedCard.stage !== targetStage) {
      updateStageMutation.mutate({ id: draggedCard.id, stage: targetStage });
    }
    setDraggedCard(null);
  };

  const getStageColor = (color: string) => {
    const colors: Record<string, string> = {
      gray: 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600',
      blue: 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700',
      purple: 'bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700',
      indigo: 'bg-indigo-100 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700',
      orange: 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700',
      green: 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700',
      emerald: 'bg-emerald-100 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700',
      red: 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700',
    };
    return colors[color] || colors.gray;
  };

  const getStageHeaderColor = (color: string) => {
    const colors: Record<string, string> = {
      gray: 'text-gray-700 dark:text-gray-300',
      blue: 'text-blue-700 dark:text-blue-300',
      purple: 'text-purple-700 dark:text-purple-300',
      indigo: 'text-indigo-700 dark:text-indigo-300',
      orange: 'text-orange-700 dark:text-orange-300',
      green: 'text-green-700 dark:text-green-300',
      emerald: 'text-emerald-700 dark:text-emerald-300',
      red: 'text-red-700 dark:text-red-300',
    };
    return colors[color] || colors.gray;
  };

  if (jobLoading || pipelineLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64" />
        <div className="flex gap-4 overflow-x-auto">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="w-72 h-96 bg-gray-200 dark:bg-gray-700 rounded-xl flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  // Filter out rejected if not showing
  const visibleStages = showRejected
    ? PIPELINE_STAGE_CONFIG
    : PIPELINE_STAGE_CONFIG.filter(s => s.id !== 'rejected');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Link to="/jobs" className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-2 inline-block">
            &larr; Back to Jobs
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Candidate Pipeline</h1>
          {job && (
            <p className="text-gray-600 dark:text-gray-400">
              Managing candidates for: <span className="font-medium text-gray-900 dark:text-white">{job.title}</span>
            </p>
          )}
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showRejected}
              onChange={(e) => setShowRejected(e.target.checked)}
              className="rounded text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">Show Rejected</span>
          </label>

          <Link
            to="/match/$jobId"
            params={{ jobId }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="w-4 h-4" />
            Add Candidates
          </Link>
        </div>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {PIPELINE_STAGE_CONFIG.filter(s => s.id !== 'rejected').map(stage => (
            <div
              key={stage.id}
              className={`p-3 rounded-lg border ${getStageColor(stage.color)}`}
            >
              <p className={`text-lg font-bold ${getStageHeaderColor(stage.color)}`}>
                {stats.stageCounts?.[stage.id as PipelineStage] || 0}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">{stage.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {visibleStages.map(stage => (
          <div
            key={stage.id}
            className={`w-72 flex-shrink-0 rounded-xl border ${getStageColor(stage.color)}`}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage.id as PipelineStage)}
          >
            {/* Stage Header */}
            <div className={`p-3 border-b border-inherit`}>
              <div className="flex items-center justify-between">
                <h3 className={`font-semibold ${getStageHeaderColor(stage.color)}`}>
                  {stage.label}
                </h3>
                <span className={`text-sm ${getStageHeaderColor(stage.color)}`}>
                  {candidatesByStage[stage.id as PipelineStage]?.length || 0}
                </span>
              </div>
            </div>

            {/* Cards Container */}
            <div className="p-2 space-y-2 min-h-[200px] max-h-[calc(100vh-350px)] overflow-y-auto">
              {candidatesByStage[stage.id as PipelineStage]?.map(candidate => (
                <CandidateCardComponent
                  key={candidate.id}
                  candidate={candidate}
                  onDragStart={handleDragStart}
                  onClick={() => setSelectedCandidate(candidate)}
                  isDragging={draggedCard?.id === candidate.id}
                />
              ))}

              {candidatesByStage[stage.id as PipelineStage]?.length === 0 && (
                <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
                  No candidates
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Candidate Detail Modal */}
      {selectedCandidate && (
        <CandidateDetailModal
          candidate={selectedCandidate}
          jobId={jobId}
          onClose={() => setSelectedCandidate(null)}
        />
      )}
    </div>
  );
}

function CandidateCardComponent({
  candidate,
  onDragStart,
  onClick,
  isDragging,
}: {
  candidate: CandidateCard;
  onDragStart: (e: React.DragEvent, card: CandidateCard) => void;
  onClick: () => void;
  isDragging: boolean;
}) {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, candidate)}
      onClick={onClick}
      className={`bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="font-medium text-gray-900 dark:text-white text-sm">
            {candidate.candidate.name}
          </h4>
          {candidate.candidate.email && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[180px]">
              {candidate.candidate.email}
            </p>
          )}
        </div>
        <RatingStars rating={candidate.rating} size="sm" />
      </div>

      {/* Top Skills */}
      <div className="flex flex-wrap gap-1 mb-2">
        {candidate.candidate.skills.slice(0, 3).map((skill, i) => (
          <span
            key={i}
            className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs"
          >
            {skill.skill}
          </span>
        ))}
        {candidate.candidate.skills.length > 3 && (
          <span className="px-1.5 py-0.5 text-gray-400 dark:text-gray-500 text-xs">
            +{candidate.candidate.skills.length - 3}
          </span>
        )}
      </div>

      {/* Interview indicator */}
      {candidate.interviewsCount > 0 && (
        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
          <CalendarIcon className="w-3 h-3" />
          <span>{candidate.interviewsCount} interview{candidate.interviewsCount > 1 ? 's' : ''}</span>
        </div>
      )}
    </div>
  );
}

function CandidateDetailModal({
  candidate,
  jobId,
  onClose,
}: {
  candidate: CandidateCard;
  jobId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [rating, setRating] = useState(candidate.rating);

  const { data: detailData } = useQuery({
    queryKey: ['pipeline-detail', candidate.id],
    queryFn: () => api.getPipelineEntry(candidate.id),
  });

  const { data: interviewsData } = useQuery({
    queryKey: ['pipeline-interviews', candidate.id],
    queryFn: () => api.getPipelineInterviews(candidate.id),
  });

  const updateMutation = useMutation({
    mutationFn: (data: { notes?: string; rating?: number }) => api.updatePipeline(candidate.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipeline', jobId] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-detail', candidate.id] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => api.rejectCandidate(candidate.id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipeline', jobId] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-stats', jobId] });
      onClose();
    },
  });

  const detail = detailData?.data;
  const interviews = interviewsData?.data || [];

  const handleSaveNotes = () => {
    updateMutation.mutate({ notes, rating });
  };

  const handleReject = () => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      rejectMutation.mutate(reason);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {candidate.candidate.name}
            </h2>
            {candidate.candidate.email && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{candidate.candidate.email}</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowScheduleModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <CalendarIcon className="w-4 h-4" />
              Schedule Interview
            </button>
            <button
              onClick={() => setShowEmailModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              <MailIcon className="w-4 h-4" />
              Send Email
            </button>
            <Link
              to="/cvs/$cvId"
              params={{ cvId: candidate.cvId }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              <DocumentIcon className="w-4 h-4" />
              View CV
            </Link>
            <button
              onClick={handleReject}
              className="flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50"
            >
              <XCircleIcon className="w-4 h-4" />
              Reject
            </button>
          </div>

          {/* Skills */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Skills</h3>
            <div className="flex flex-wrap gap-2">
              {candidate.candidate.skills.map((skill, i) => (
                <span
                  key={i}
                  className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm"
                >
                  {skill.skill} ({skill.years}y)
                </span>
              ))}
            </div>
          </div>

          {/* Rating */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Rating</h3>
            <RatingStars rating={rating} size="lg" editable onRatingChange={setRating} />
          </div>

          {/* Notes */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Notes</h3>
            <textarea
              value={notes || detail?.notes || ''}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this candidate..."
              className="w-full h-24 px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400"
            />
            <button
              onClick={handleSaveNotes}
              disabled={updateMutation.isPending}
              className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Notes'}
            </button>
          </div>

          {/* Interviews */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Interviews ({interviews.length})
            </h3>
            {interviews.length > 0 ? (
              <div className="space-y-2">
                {interviews.map((interview: any) => (
                  <div
                    key={interview.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{interview.title}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(interview.scheduledAt).toLocaleString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${getInterviewStatusColor(interview.status)}`}>
                      {interview.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-sm">No interviews scheduled yet.</p>
            )}
          </div>

          {/* Stage History */}
          {detail?.stageHistory && detail.stageHistory.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Stage History</h3>
              <div className="space-y-2">
                {detail.stageHistory.map((entry: any, i: number) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-primary-500" />
                    <span className="font-medium text-gray-900 dark:text-white">{entry.stage}</span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {new Date(entry.entered_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Schedule Interview Modal */}
        {showScheduleModal && (
          <ScheduleInterviewModal
            pipelineId={candidate.id}
            candidateName={candidate.candidate.name}
            candidateEmail={candidate.candidate.email || ''}
            onClose={() => setShowScheduleModal(false)}
            onSuccess={() => {
              setShowScheduleModal(false);
              queryClient.invalidateQueries({ queryKey: ['pipeline-interviews', candidate.id] });
            }}
          />
        )}

        {/* Email Modal */}
        {showEmailModal && (
          <EmailComposerModal
            pipelineId={candidate.id}
            to={candidate.candidate.email || ''}
            candidateName={candidate.candidate.name}
            onClose={() => setShowEmailModal(false)}
            onSuccess={() => setShowEmailModal(false)}
          />
        )}
      </div>
    </div>
  );
}

function ScheduleInterviewModal({
  pipelineId,
  candidateName,
  candidateEmail,
  onClose,
  onSuccess,
}: {
  pipelineId: string;
  candidateName: string;
  candidateEmail: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [title, setTitle] = useState('');
  const [type, setType] = useState<'phone' | 'video' | 'onsite'>('video');
  const [scheduledAt, setScheduledAt] = useState('');
  const [duration, setDuration] = useState(60);
  const [location, setLocation] = useState('');
  const [meetingLink, setMeetingLink] = useState('');

  const scheduleMutation = useMutation({
    mutationFn: () => api.scheduleInterview({
      pipeline_id: pipelineId,
      title,
      type,
      scheduled_at: new Date(scheduledAt).toISOString(),
      duration_minutes: duration,
      location: location || undefined,
      meeting_link: meetingLink || undefined,
      interviewers: [], // Would be populated from team members
      candidate_email: candidateEmail,
      candidate_name: candidateName,
    }),
    onSuccess,
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Schedule Interview</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Interview Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Phone Screen, Technical Interview"
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as typeof type)}
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="phone">Phone Call</option>
              <option value="video">Video Call</option>
              <option value="onsite">On-site</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date & Time
            </label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Duration (minutes)
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
            </select>
          </div>

          {type === 'video' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Meeting Link
              </label>
              <input
                type="url"
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
                placeholder="https://zoom.us/j/..."
                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          )}

          {type === 'onsite' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Location
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Office address"
                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => scheduleMutation.mutate()}
            disabled={!title || !scheduledAt || scheduleMutation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {scheduleMutation.isPending ? 'Scheduling...' : 'Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmailComposerModal({
  pipelineId,
  to,
  candidateName,
  jobTitle,
  onClose,
  onSuccess,
}: {
  pipelineId: string;
  to: string;
  candidateName: string;
  jobTitle?: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [showAiPanel, setShowAiPanel] = useState(true);
  const [aiPurpose, setAiPurpose] = useState<'interview_invite' | 'follow_up' | 'rejection' | 'offer' | 'general'>('interview_invite');
  const [aiTone, setAiTone] = useState<'formal' | 'friendly' | 'professional'>('professional');
  const [aiContext, setAiContext] = useState('');

  const sendMutation = useMutation({
    mutationFn: () => api.sendEmail({
      to,
      subject,
      body,
      pipeline_id: pipelineId,
      placeholders: { candidate_name: candidateName },
    }),
    onSuccess,
  });

  const aiComposeMutation = useMutation({
    mutationFn: () => api.aiComposeEmail({
      purpose: aiPurpose,
      candidateName,
      candidateEmail: to,
      jobTitle,
      additionalContext: aiContext || undefined,
      tone: aiTone,
    }),
    onSuccess: (data) => {
      if (data.data) {
        setSubject(data.data.subject);
        setBody(data.data.body);
        setShowAiPanel(false);
      }
    },
  });

  const purposeLabels = {
    interview_invite: 'Interview Invitation',
    follow_up: 'Follow Up',
    rejection: 'Rejection (Professional)',
    offer: 'Job Offer',
    general: 'General Outreach',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Compose Email</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* AI Compose Panel */}
          {showAiPanel && (
            <div className="bg-gradient-to-r from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-primary-200 dark:border-primary-800">
              <div className="flex items-center gap-2 mb-3">
                <SparklesIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <h4 className="font-medium text-gray-900 dark:text-white">AI Email Composer</h4>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Purpose</label>
                  <select
                    value={aiPurpose}
                    onChange={(e) => setAiPurpose(e.target.value as any)}
                    className="w-full px-3 py-2 text-sm border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {Object.entries(purposeLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Tone</label>
                  <select
                    value={aiTone}
                    onChange={(e) => setAiTone(e.target.value as any)}
                    className="w-full px-3 py-2 text-sm border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="professional">Professional</option>
                    <option value="friendly">Friendly</option>
                    <option value="formal">Formal</option>
                  </select>
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Additional Context (optional)</label>
                <input
                  type="text"
                  value={aiContext}
                  onChange={(e) => setAiContext(e.target.value)}
                  placeholder="e.g., interview next Tuesday at 2pm..."
                  className="w-full px-3 py-2 text-sm border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => aiComposeMutation.mutate()}
                  disabled={aiComposeMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  <SparklesIcon className="w-4 h-4" />
                  {aiComposeMutation.isPending ? 'Generating...' : 'Generate Email'}
                </button>
                <button
                  onClick={() => setShowAiPanel(false)}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Write manually
                </button>
              </div>

              {aiComposeMutation.isError && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  Failed to generate email. Please try again.
                </p>
              )}
            </div>
          )}

          {/* Toggle AI Panel */}
          {!showAiPanel && (
            <button
              onClick={() => setShowAiPanel(true)}
              className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              <SparklesIcon className="w-4 h-4" />
              Use AI to compose email
            </button>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To</label>
            <input
              type="email"
              value={to}
              disabled
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your message..."
              rows={8}
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => sendMutation.mutate()}
            disabled={!subject || !body || sendMutation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {sendMutation.isPending ? 'Sending...' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function RatingStars({
  rating,
  size = 'md',
  editable = false,
  onRatingChange,
}: {
  rating: number;
  size?: 'sm' | 'md' | 'lg';
  editable?: boolean;
  onRatingChange?: (rating: number) => void;
}) {
  const sizeClass = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  }[size];

  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          onClick={() => editable && onRatingChange?.(star)}
          disabled={!editable}
          className={editable ? 'cursor-pointer' : 'cursor-default'}
        >
          <StarIcon
            className={`${sizeClass} ${
              star <= rating
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-300 dark:text-gray-600'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

function getInterviewStatusColor(status: string): string {
  const colors: Record<string, string> = {
    scheduled: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    confirmed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    completed: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    no_show: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
  };
  return colors[status] || colors.scheduled;
}

// Icons
function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function XCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  );
}
